import { ref, watch, onMounted, onBeforeUnmount, reactive } from "vue"
import { onBeforeRouteLeave, onBeforeRouteUpdate } from "vue-router"
import { ask as askDialog, showForCopy } from "./lib/confirm.js"

// ---------------------------------------------------------------------------
// Delayed loading flag — anti-flicker
// ---------------------------------------------------------------------------

/**
 * Returns a ref that only becomes true once `source` has stayed truthy for
 * `delay` ms. Fast loads (the common case against a local backend) finish
 * before the timer fires, so the loading indicator never flashes — no
 * one-frame "Загрузка…" and no accompanying layout jump.
 *
 *   const loading = ref(true)
 *   const loadingShown = useDelayedLoading(loading)
 *   // template: gate the visible indicator on `loadingShown`, not `loading`
 */
export function useDelayedLoading(source, delay = 250) {
  const shown = ref(false)
  let timer = null
  watch(source, (v) => {
    clearTimeout(timer)
    if (v) timer = setTimeout(() => { shown.value = true }, delay)
    else shown.value = false
  }, { immediate: true })
  return shown
}

/**
 * Standard data-loading lifecycle for a view: runs `fetcher` on mount, tracks
 * loading/error, and exposes an anti-flicker `loadingShown` (gate the visible
 * spinner on it) plus a `reload`. The fetcher assigns to the view's own refs.
 *
 *   const clients = ref([])
 *   const { loading, loadingShown, error, reload } = useAsyncData(async () => {
 *     clients.value = await API.get("/api/n/clients")
 *   })
 *   // template: <div v-if="loading"><Spinner v-if="loadingShown" /></div>
 */
/**
 * One-at-a-time actions keyed by what they act on: `run("del:12", fn)` does
 * nothing while the same key is running, and `busy["del:12"]` is true
 * meanwhile (bind it to the button's :disabled). Stops double submits, e.g. a
 * second DELETE fired by a double click.
 */
export function useBusy() {
  const busy = reactive({})
  const run = async (key, fn) => {
    if (busy[key]) return undefined
    busy[key] = true
    try { return await fn() } finally { delete busy[key] }
  }
  return { busy, run }
}

export const LEAVE_MESSAGE = "Есть несохранённые изменения. Уйти без сохранения?"

/**
 * Ask before leaving while `isDirty()` is true: in-app navigation (including
 * to another client via a family link, which remounts the page) and closing
 * or reloading the tab.
 */
export function useLeaveGuard(isDirty, {
  ask = (m) => askDialog({ title: m, message: "Изменения на этой странице пропадут.", confirmLabel: "Уйти без сохранения", cancelLabel: "Остаться", danger: true }),
} = {}) {
  const guard = () => !isDirty() || ask(LEAVE_MESSAGE)
  onBeforeRouteLeave(guard)
  onBeforeRouteUpdate((to, from) => to.path === from.path || guard())
  const onUnload = (e) => { if (isDirty()) { e.preventDefault(); e.returnValue = "" } }
  onMounted(() => window.addEventListener("beforeunload", onUnload))
  onBeforeUnmount(() => window.removeEventListener("beforeunload", onUnload))
}

export function useAsyncData(fetcher, { immediate = true } = {}) {
  const loading = ref(immediate)
  const loadingShown = useDelayedLoading(loading)
  const error = ref(null)
  const reload = async () => {
    loading.value = true
    error.value = null
    try { await fetcher() }
    catch (e) { error.value = e; console.error(e) }
    finally { loading.value = false }
  }
  if (immediate) onMounted(reload)
  return { loading, loadingShown, error, reload }
}

// ---------------------------------------------------------------------------
// Date formatting — backend sends "YYYY-MM-DD HH:MM:SS" strings
// ---------------------------------------------------------------------------

const MONTHS = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
]

/** "2026-06-28 16:29:08" → "28 июня 2026, 16:29" (falls back to the raw string). */
export function fmtDateTime(str) {
  if (!str) return ""
  const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/)
  if (!m) return String(str)
  return `${+m[3]} ${MONTHS[+m[2] - 1]} ${m[1]}, ${m[4]}:${m[5]}`
}

/** "2026-06-28 16:29:08" → "28 июня 2026". */
export function fmtDate(str) {
  if (!str) return ""
  const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return String(str)
  return `${+m[3]} ${MONTHS[+m[2] - 1]} ${m[1]}`
}

/**
 * Go back if there's in-app history, else navigate to a fallback. Keeps deep
 * pages (opened directly by URL) from bouncing the user out of the app.
 */
export function smartBack(router, fallback = "/n/dashboard") {
  if (window.history.state && window.history.state.back != null) router.back()
  else router.push(fallback)
}

/** Russian plural for "год": 1 → "год", 2–4 → "года", else "лет". */
export function pluralYears(n) {
  const a = Math.abs(n) % 100, b = n % 10
  if (a > 10 && a < 20) return "лет"
  if (b === 1) return "год"
  if (b > 1 && b < 5) return "года"
  return "лет"
}

// ---------------------------------------------------------------------------
// Toasts — lightweight replacement for alert(); rendered globally in app.js
// ---------------------------------------------------------------------------

export const toasts = ref([])
let _toastId = 0

/**
 * Copy `text` to the clipboard. Where that's refused (plain-http origin, denied
 * permission, old browser) show it in a prompt the user can copy from instead.
 * Returns true only when it actually reached the clipboard.
 */
export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true }
  catch { await showForCopy(text); return false }
}

/** Show a toast. type: "error" | "success" | "info". */
// Errors stay longer (they need reading, often twice); any toast waits while
// hovered or focused, so it can't vanish mid-sentence or under the pointer.
const _TOAST_MS = { error: 8000, success: 4000, info: 5000 }
const _toastTimers = new Map()

export function toast(message, type = "error") {
  const id = ++_toastId
  toasts.value.push({ id, message, type })
  _toastTimers.set(id, setTimeout(() => dismissToast(id), _TOAST_MS[type] ?? 5000))
}

export function dismissToast(id) {
  clearTimeout(_toastTimers.get(id)); _toastTimers.delete(id)
  toasts.value = toasts.value.filter(t => t.id !== id)
}

export function holdToast(id) { clearTimeout(_toastTimers.get(id)) }

export function releaseToast(id) {
  if (!toasts.value.some((t) => t.id === id)) return
  clearTimeout(_toastTimers.get(id))
  _toastTimers.set(id, setTimeout(() => dismissToast(id), 3000))
}